import { useEffect, useMemo, useState } from "react";

import { useRouting } from "@/hooks/useRouting";
import { RoutingAdvisor } from "@/components/routing/RoutingAdvisor";
import { RoutingControls } from "@/components/routing/RoutingControls";
import { RoutingMap } from "@/components/routing/RoutingMap";
import { RoutingTimeline } from "@/components/routing/RoutingTimeline";
import { RoutingStrategies } from "@/components/routing/RoutingStrategies";
import { useCapabilities } from "@/hooks/useCapabilities";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type { RoutingQueryInput } from "@/types/routing";

export default function RoutingPage() {
  const routing = useRouting();
  const { suggestRoute } = routing;
  const [alternativesLoading, setAlternativesLoading] = useState(false);
  const { capabilities, openUpgradeModal } = useCapabilities();
  const canUseRoutingStrategies = capabilities.canUseRoutingStrategies;
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [savedRoutesOpen, setSavedRoutesOpen] = useState(false);
  const [routeName, setRouteName] = useState("");
  const [savedRoutes, setSavedRoutes] = useState<SavedRoute[]>([]);

  useEffect(() => {
    const stored = loadSavedRoutes();
    if (stored.length) {
      setSavedRoutes(stored);
    }
  }, []);

  const handleAlternatives = async () => {
    if (!canUseRoutingStrategies) {
      openUpgradeModal("Routing strategies");
      return;
    }
    setAlternativesLoading(true);
    try {
      await routing.fetchAlternatives();
    } finally {
      setAlternativesLoading(false);
    }
  };

  const hasPlan = Boolean(routing.plan?.ordered_city_names?.length);
  const needsRefresh = routing.needsRefresh;

  const saveDisabled = !hasPlan;

  const saveSummary = useMemo(() => {
    const stops = routing.plan?.ordered_city_names ?? [];
    return stops.slice(0, 4).join(" → ");
  }, [routing.plan?.ordered_city_names]);

  const stops = routing.plan?.ordered_city_names ?? [];
  const stopCount = stops.length;
  const routeDistanceMiles = routing.routeDetails?.distance_km
    ? Math.round(routing.routeDetails.distance_km * 0.621371)
    : null;
  const routeHours = routing.routeDetails?.duration_hours ? Math.round(routing.routeDetails.duration_hours) : null;

  const handleSaveRoute = () => {
    if (!routing.plan) return;
    const trimmed = routeName.trim();
    const name = trimmed || `Route ${new Date().toLocaleDateString()}`;
    const saved: SavedRoute = {
      id: createSavedRouteId(),
      name,
      savedAt: new Date().toISOString(),
      query: routing.query,
      summary: routing.plan.ordered_city_names ?? [],
    };
    const next = [saved, ...savedRoutes].slice(0, 20);
    setSavedRoutes(next);
    persistSavedRoutes(next);
    setRouteName("");
    setSaveDialogOpen(false);
    setSavedRoutesOpen(true);
  };

  const handleLoadRoute = async (route: SavedRoute) => {
    routing.updateQuery(route.query, false);
    await routing.suggestRoute(route.query);
    setSavedRoutesOpen(false);
  };

  return (
    <div className="aa-page-shell space-y-6">
      <div className="rounded-2xl border border-border/40 bg-transparent p-4 text-sm text-foreground/80">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Routing studio</p>
            <p className="text-lg font-semibold text-foreground">Routing Agent</p>
            <p className="text-xs text-muted-foreground">
              Cluster CRM venues, evaluate drive times, and surface bookable windows for touring plans.
            </p>
            <p className="mt-2 text-xs text-muted-foreground">
              Build the plan, audit the drive health, and ship leads without hopping tabs.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setSavedRoutesOpen(true)}>
              View Saved Routes
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSaveDialogOpen(true)} disabled={saveDisabled}>
              Save Route
            </Button>
            <Button size="sm" onClick={() => suggestRoute()} disabled={routing.loading}>
              Generate Route
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
          <div className="rounded-full border border-border/50 bg-background/30 px-3 py-1 text-foreground/80">
            {stopCount ? `${stopCount} stops` : "No stops yet"}
          </div>
          <div className="rounded-full border border-border/50 bg-background/30 px-3 py-1 text-foreground/80">
            {routeHours ? `${routeHours} hrs drive` : "Drive time pending"}
          </div>
          <div className="rounded-full border border-border/50 bg-background/30 px-3 py-1 text-foreground/80">
            {routeDistanceMiles ? `${routeDistanceMiles} mi` : "Distance pending"}
          </div>
          {needsRefresh ? (
            <div className="rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-primary">
              Recalculate to sync leads
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 p-0 xl:grid-cols-12">
        <section className="space-y-6 xl:col-span-4">
          <RoutingControls
            query={routing.query}
            onQueryChange={routing.updateQuery}
            onSubmit={() => routing.suggestRoute()}
            loading={routing.loading}
          />
        </section>

        <section className="space-y-6 xl:col-span-8">
          <RoutingMap
            plan={routing.plan}
            useRealRouting={routing.useRealRouting}
            onToggleRealRouting={routing.setUseRealRouting}
            routeSummary={routing.routeDetails}
            routeError={routing.routeError}
            maxDriveHours={routing.query.max_drive_hours ?? null}
            needsRefresh={routing.needsRefresh}
          />
          <RoutingStrategies
            strategies={routing.strategies ?? []}
            activeStrategy={routing.activeStrategy}
            onSelect={routing.setStrategy}
            onRefresh={handleAlternatives}
            refreshing={alternativesLoading}
            recommendedCount={routing.plan?.ordered_city_names.length ?? 0}
            recommendedPlan={routing.canonicalPlan ?? routing.plan}
          />
          <RoutingTimeline
            plan={routing.plan}
            onReorder={routing.reorderPlan}
            onPin={(label) => void routing.pinCity(label)}
            needsRefresh={routing.needsRefresh}
            onRecalculate={() => routing.suggestRoute()}
            onGenerateLead={routing.generateLeads}
            leadStatus={routing.leadStatus}
          />
          <RoutingAdvisor
            plan={routing.plan}
            advice={routing.advisor}
            loading={routing.advisorLoading}
            onGenerate={routing.requestAdvice}
          />
        </section>
      </div>

      <Dialog open={mapDialogOpen} onOpenChange={setMapDialogOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Routing map</DialogTitle>
            <DialogDescription>Review the current routing path and drive-time health.</DialogDescription>
          </DialogHeader>
          {mapDialogOpen ? (
            <RoutingMap
              plan={routing.plan}
              useRealRouting={routing.useRealRouting}
              onToggleRealRouting={routing.setUseRealRouting}
              routeSummary={routing.routeDetails}
              routeError={routing.routeError}
              maxDriveHours={routing.query.max_drive_hours ?? null}
              needsRefresh={routing.needsRefresh}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Save routing</DialogTitle>
            <DialogDescription>Give this routing plan a name to reuse later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Input
              value={routeName}
              onChange={(event) => setRouteName(event.target.value)}
              placeholder="Spring Midwest run"
            />
            {routing.plan?.ordered_city_names?.length ? (
              <p className="text-xs text-muted-foreground">
                {routing.plan.ordered_city_names.length} stops • {saveSummary}
              </p>
            ) : null}
          </div>
          <DialogFooter className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRoute} disabled={saveDisabled}>
              Save route
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={savedRoutesOpen} onOpenChange={setSavedRoutesOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Saved routes</DialogTitle>
            <DialogDescription>Reopen a saved routing plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {savedRoutes.length ? (
              savedRoutes.map((route) => (
                <div
                  key={route.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border/50 bg-background/40 p-4"
                >
                  <div>
                    <p className="text-sm font-semibold text-foreground">{route.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatSavedRouteDate(route.savedAt)} • {route.summary.length} stops
                    </p>
                    {route.summary.length ? (
                      <p className="mt-1 text-xs text-foreground/70">
                        {route.summary.slice(0, 4).join(" → ")}
                        {route.summary.length > 4 ? "…" : ""}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleLoadRoute(route)}>
                      Load
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">No saved routes yet.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

type SavedRoute = {
  id: string;
  name: string;
  savedAt: string;
  query: RoutingQueryInput;
  summary: string[];
};

const SAVED_ROUTES_KEY = "americana.savedRoutes";

function loadSavedRoutes(): SavedRoute[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(SAVED_ROUTES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedRoute[];
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((entry) => entry && entry.id && entry.name);
  } catch {
    return [];
  }
}

function persistSavedRoutes(routes: SavedRoute[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(SAVED_ROUTES_KEY, JSON.stringify(routes));
}

function createSavedRouteId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function formatSavedRouteDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
}
